import React, { useEffect, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { useDashboardStore } from '@/stores/dashboardStore';
import { useAutoRefresh } from '@/hooks';
import { Loading, ErrorMessage, Button } from '@/components/common';
import { LOW_STOCK_FILTERS } from '@/utils/constants';

type LowStockFilter = typeof LOW_STOCK_FILTERS[keyof typeof LOW_STOCK_FILTERS];

export const LowStockPage: React.FC = () => {
  const [filter, setFilter] = useState<LowStockFilter>(LOW_STOCK_FILTERS.ALL);
  const { lowStockBatteries, isLoading, error, fetchLowStock } = useDashboardStore();

  useEffect(() => {
    fetchLowStock(filter);
  }, [filter, fetchLowStock]);

  useAutoRefresh(() => {
    fetchLowStock(filter);
  }, [filter]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'critical':
        return 'badge-danger';
      case 'low':
        return 'badge-warning';
      default:
        return 'badge-info';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'critical':
        return 'حرج';
      case 'low':
        return 'منخفض';
      default:
        return 'متوسط';
    }
  };

  if (isLoading && lowStockBatteries.length === 0) {
    return <Loading size="lg" text="جاري تحميل البيانات..." />;
  }

  if (error) {
    return <ErrorMessage message={error} onRetry={() => fetchLowStock(filter)} />;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-primary">المخزون المنخفض</h1>
      </div>

      <div className="card">
        <div className="flex gap-2 mb-6">
          <Button
            variant={filter === LOW_STOCK_FILTERS.ALL ? 'primary' : 'secondary'}
            size="sm"
            onClick={() => setFilter(LOW_STOCK_FILTERS.ALL)}
          >
            الكل
          </Button>
          <Button
            variant={filter === LOW_STOCK_FILTERS.CRITICAL ? 'primary' : 'secondary'}
            size="sm"
            onClick={() => setFilter(LOW_STOCK_FILTERS.CRITICAL)}
          >
            حرج
          </Button>
          <Button
            variant={filter === LOW_STOCK_FILTERS.LOW ? 'primary' : 'secondary'}
            size="sm"
            onClick={() => setFilter(LOW_STOCK_FILTERS.LOW)}
          >
            منخفض
          </Button>
        </div>

        {lowStockBatteries.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <th>الماركة</th>
                  <th>الحجم</th>
                  <th>مخزون المستودع</th>
                  <th>مخزون السائقين</th>
                  <th>المخزون الكلي</th>
                  <th>الحالة</th>
                </tr>
              </thead>
              <tbody>
                {lowStockBatteries.map((battery) => (
                  <tr key={battery.id}>
                    <td className="font-semibold">{battery.brand}</td>
                    <td>{battery.size}</td>
                    <td>{battery.totalWarehouseStock}</td>
                    <td>{battery.totalDriverStock}</td>
                    <td className="font-semibold">{battery.totalStock}</td>
                    <td>
                      <span className={`badge ${getStatusColor(battery.status)}`}>
                        {getStatusLabel(battery.status)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-12">
            <AlertTriangle className="h-16 w-16 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400">لا توجد بطاريات منخفضة المخزون</p>
          </div>
        )}
      </div>
    </div>
  );
};